import React, { FC } from 'react';
import { Button, Tooltip } from '@myua/kit';
import * as sharingActions from '../actions';
import { useDispatch, useSelector } from 'react-redux';
import { Asset, isAssetDownloadAllowed } from '../../../api';
import { getCanCreateSharingLink, getShareInProgress } from '../selectors';
import { ShareContext } from '../model';
import { getAssetLabLogger } from '../../app/logs';
import { Link21 } from '@myua/icons/dist/bulk16';
import { DEMO_UNAVAILABLE_TOOLTIP } from '../../../../general/model';

export const ShareAssetButton: FC<{ asset: Asset }> = ({ asset }) => {
  const dispatch = useDispatch();
  const shareInProgress = useSelector(getShareInProgress);

  // Если данный ассет уже в обработке или если все ассеты в обработке, то не позволяем нажимать кнопку.
  const loading =
    shareInProgress[ShareContext.allAssets] || shareInProgress[ShareContext.asset][asset.id];

  const assetLabLogger = useSelector(getAssetLabLogger);

  const canShare = useSelector(getCanCreateSharingLink);

  // Файл с запрещённым скачиванием (шрифт под модерацией/отклонённый) шарить нельзя:
  // в публичной ссылке такие файлы всё равно не отображаются (MYUA-16456).
  const downloadAllowed = isAssetDownloadAllowed(asset);

  const tooltip = !canShare
    ? DEMO_UNAVAILABLE_TOOLTIP
    : !downloadAllowed
      ? 'This file is unavailable for sharing'
      : undefined;

  const button = (
    <Button
      className="al-btn-blue-icon"
      disabled={!canShare || !downloadAllowed}
      loading={canShare && downloadAllowed && loading}
      onClick={() => {
        const assetIds = [asset.id];
        dispatch(sharingActions.shareAssetsClicked({ assetIds }));
        assetLabLogger({
          action: 'Assets',
          sub_action: 'Share',
          extra: {
            // asset_ids: assetIds,
            text_1: 'single',
          },
        });
      }}
      icon={<Link21 />}
    >
      Share file
    </Button>
  );

  return tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button;
};

export const ShareAssetButtonMultiple: FC<{ assets: Asset[] }> = ({ assets }) => {
  const dispatch = useDispatch();

  const shareInProgress = useSelector(getShareInProgress);

  // Если хотя бы один ассет уже в обработке, то не позволяем нажимать кнопку.
  // Блокируем ее, а не ставим loading, чтобы спиннер не смущал юзера, т.к. не все выбранные
  // ассеты могут находиться в обработке.
  const disabled =
    shareInProgress[ShareContext.allAssets] ||
    assets.some(({ id }) => shareInProgress[ShareContext.asset][id]);

  const assetLabLogger = useSelector(getAssetLabLogger);

  const canShare = useSelector(getCanCreateSharingLink);

  const button = (
    <Button
      disabled={!canShare || disabled}
      onClick={() => {
        const assetIds = assets.map(({ id }) => id);
        const selectAll = assets.length === 0;
        dispatch(sharingActions.shareAssetsClicked(selectAll ? { selectAll } : { assetIds }));
        assetLabLogger({
          action: 'Assets',
          sub_action: 'Share',
          extra: {
            // asset_ids: assetIds,
            text_1: selectAll ? 'all' : 'multiple',
          },
        });
      }}
    >
      Share
    </Button>
  );

  return canShare ? button : <Tooltip title={DEMO_UNAVAILABLE_TOOLTIP}>{button}</Tooltip>;
};
